export type ParsedAddress = {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zipcode: string;
  formatted_address: string;
  latitude?: number;
  longitude?: number;
};

/**
 * Extracts the street address from a formatted address string.
 * Assumes format like "123 Main St, City, ST 12345"
 */
const extractStreetFromFormatted = (formattedAddress: string): string => {
  const firstPart = formattedAddress.split(',')[0]?.trim() || '';
  // Only use it if it looks like a street address (starts with a number or contains common street words)
  const looksLikeStreet =
    /^\d+\s/.test(firstPart) ||
    /\b(st|street|ave|avenue|rd|road|dr|drive|ln|lane|blvd|way|ct|court|pl|place)\b/i.test(firstPart);
  return looksLikeStreet ? firstPart : '';
};

/**
 * Extracts structured address components from Google address_components.
 * @param userInput - Optional user's typed input, used as fallback when Google returns city-level data
 */
export const extractAddress = (
  addressComponents: google.maps.GeocoderAddressComponent[],
  formattedAddress: string,
  userInput?: string,
): ParsedAddress => {
  const getComponent = (type: string, useShortName = false) => {
    const comp = addressComponents.find((c) => c.types.includes(type));
    return comp ? (useShortName ? comp.short_name : comp.long_name) : '';
  };

  const streetNumber = getComponent('street_number');
  const route = getComponent('route');
  const premise = getComponent('premise');

  let line1 = '';
  if (streetNumber && route) {
    line1 = `${streetNumber} ${route}`;
  } else if (route) {
    line1 = route;
  } else if (premise) {
    line1 = premise;
  }

  // Fallback: try to extract street from user input first (more reliable), then formatted address
  if (!line1) {
    if (userInput) {
      line1 = extractStreetFromFormatted(userInput);
    }
    if (!line1) {
      line1 = extractStreetFromFormatted(formattedAddress);
    }
  }

  // Try to extract zipcode - check user input first, then formatted address
  let zipcode = getComponent('postal_code') || getComponent('postal_code_prefix');
  if (!zipcode) {
    const inputToSearch = userInput || formattedAddress;
    const zipMatch = inputToSearch.match(/\b(\d{5}(?:-\d{4})?)\b/);
    if (zipMatch) {
      zipcode = zipMatch[1];
    }
  }

  return {
    address_line_1: line1.trim(),
    address_line_2: getComponent('subpremise'),
    city:
      getComponent('locality') ||
      getComponent('sublocality_level_1') ||
      getComponent('sublocality') ||
      getComponent('postal_town') ||
      getComponent('administrative_area_level_2') ||
      getComponent('neighborhood'),
    state: getComponent('administrative_area_level_1', true),
    zipcode,
    formatted_address: formattedAddress || userInput || '',
  };
};

/**
 * Validates that a parsed address has all required components for the API.
 */
export const isValidParsedAddress = (address: ParsedAddress): boolean => {
  return Boolean(
    address.address_line_1?.trim() && address.city?.trim() && address.state?.trim() && address.zipcode?.trim(),
  );
};
